import React from "react"
import { Box, Grid, Tooltip, Typography } from "@mui/material"

import {
	AppButtonIcon, AppCardTitle, AppCrudTable, AppEmptyLine, AppPillToggle, AppResultsSkeleton, AppStatusPill, AppToolbarCreateButton,
	AppValueChip,
} from "@components"
import { useToast } from "@hooks"
import { dsColorNew } from "@palette"
import { formatDay } from "@components/News/@services/dates"
import { deleteNewsAnnouncement, fetchNewsAnnouncements, setNewsActive } from "../@hooks"
import { announcementState, audienceEntries, audienceSummary, DISPLAY_MODES, seenBy, STATE_FILTER_OPTIONS, STATE_LABELS } from "../@services/news"
import { isLoadingNews, newsAnnouncements, newsAudiences, newsFilters, newsListFailed, openNewsEditor } from "../@signals"


function AudienceCell({ row }) {
	if (row.audience_label === 'Everyone') return <AppValueChip tone="accent">Everyone</AppValueChip>

	return <Tooltip title={row.audience_names.join(', ')} arrow>
		<span><AppValueChip>{row.audience_label}</AppValueChip></span>
	</Tooltip>
}

function WindowCell({ row }) {
	return <Box>
		<Typography sx={{ fontSize: 13, color: dsColorNew.textDark }}>{formatDay(row.start_at) || '—'}</Typography>
		<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>→ {row.end_at ? formatDay(row.end_at) : 'No expiry'}</Typography>
	</Box>
}

function ActionsCell({ row, confirm }) {
	const { showToast } = useToast()

	const toggle = () => setNewsActive(row.id_announcement, !row.active)
		.then(() => showToast.success(row.active ? 'Announcement retracted' : 'Announcement published'))
		.catch(() => showToast.error('The change could not be saved'))

	const remove = async () => {
		const ok = await confirm({
			title: 'Delete this announcement?',
			body: `“${row.title}” and its record of who read it are deleted for good. To simply stop showing it, retract it instead — that keeps the history.`,
			confirmLabel: 'Delete',
			destructive: true,
		})
		if (!ok) return
		deleteNewsAnnouncement(row.id_announcement)
			.then(() => showToast.success('Announcement deleted'))
			.catch(() => showToast.error('It could not be deleted'))
	}

	return <Box sx={{ display: 'flex', justifyContent: 'center' }}>
		<AppButtonIcon icon="pen" tooltip="Edit" onClick={() => openNewsEditor(row)}/>
		<AppButtonIcon
			icon={row.active ? 'eye-slash' : 'eye'}
			tooltip={row.active ? 'Retract — stops showing, keeps the history' : 'Publish'}
			onClick={toggle}
		/>
		<AppButtonIcon icon="trash-can" tooltip="Delete permanently" onClick={remove}/>
	</Box>
}

export default function NewsManagementTable({ confirm }) {
	// The audience names go INTO the rows: TableSeeker memoises its columns on
	// JSON.stringify(columns, data), so a cell function reading the audiences keeps the list it saw
	// first. The page loads the audiences before the listing (index.jsx), so the rows carry the names.
	const audiences = newsAudiences.value
	const rows = newsAnnouncements.value.map(row => {
		const { companies, users } = audienceEntries(row, audiences)
		return {
			...row,
			audience_label: audienceSummary(row),
			audience_names: [...companies, ...users].map(entry => entry.enabled ? entry.name : `${entry.name} (disabled)`),
		}
	})

	const setState = state => {
		newsFilters.value = { ...newsFilters.value, state }
		fetchNewsAnnouncements()
	}

	const title = <AppCardTitle icon="bullhorn" title="Announcements">
		<AppPillToggle size="small" value={newsFilters.value.state} options={STATE_FILTER_OPTIONS} onChange={setState} sx={{ ml: 2 }}/>
	</AppCardTitle>

	if (isLoadingNews.value && !newsAnnouncements.value.length)
		return <Grid item xs={12}><AppResultsSkeleton rows={6} columns={5}/></Grid>

	return <Grid item xs={12}>
		<AppCrudTable
			title={title}
			data={rows}
			// a failed load is said by the note above the table, never as "no announcements yet"
			textLabels={{ body: { noMatch: newsListFailed.value ? '' : <AppEmptyLine>No announcements yet</AppEmptyLine> } }}
			options={{
				exportExcel: false,
				rowsPerPage: 25,
				rowsPerPageOptions: [25, 50, 100],
				customToolbar: () => <AppToolbarCreateButton label="New announcement" onClick={() => openNewsEditor()}/>,
			}}
			columns={[
				{
					name: 'title', label: 'Announcement',
					options: {
						customBodyRowRenderLite: (_, row) => <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
							<AppStatusPill status={STATE_LABELS[announcementState(row)]}/>
							<Typography sx={{ fontSize: 13, fontWeight: 500, color: dsColorNew.textDark }}>{row.title}</Typography>
						</Box>,
					},
				},
				{
					name: 'audience_companies', label: 'Audience',
					options: { sort: false, filter: false, customBodyRowRenderLite: (_, row) => <AudienceCell row={row}/> },
				},
				{
					name: 'start_at', label: 'Window',
					options: { filter: false, customBodyRowRenderLite: (_, row) => <WindowCell row={row}/> },
				},
				{
					name: 'display_mode', label: 'Shows',
					options: {
						customBodyRender: value => {
							const mode = DISPLAY_MODES.find(m => m.value === value)
							return <Tooltip title={mode?.hint || ''} arrow><span>{mode?.label || value}</span></Tooltip>
						},
					},
				},
				{
					name: 'seen_count', label: 'Seen by',
					options: { filter: false, setCellProps: () => ({ align: 'center' }), customBodyRender: value => seenBy(value) },
				},
				{
					name: 'id_announcement', label: 'Actions',
					options: {
						sort: false, filter: false, download: false,
						// a column without sorting prints its label left-aligned; its icons are centred
						setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
						customBodyRowRenderLite: (_, row) => <ActionsCell row={row} confirm={confirm}/>,
					},
				},
			]}
		/>
	</Grid>
}
